import { FaLocationDot, FaPhoneVolume } from "react-icons/fa6";
import {
  MdEmail,
  MdPerson,
  MdDescription,
  MdLanguage,
  MdCalendarToday,
  MdDirectionsCar,
  MdBadge,
  MdCardMembership,
  MdUpdate,
} from "react-icons/md";
import { FaIdCard } from "react-icons/fa";
import {
  convertTime,
  formatDateToLocal,
  handleApiError,
} from "../../utils/dateAndTimeConversion";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { useUpdateDriverStatusMutation } from "../../store/api/vendor/driversSlice";
import { toast } from "react-toastify";
import { server } from "../../App";

// Helper function to get status class and label
const getDriverStatusInfo = (status) => {
  if (!status) return { className: "status unknown", label: "Unknown" };

  const statusLower = status.toLowerCase();

  switch (statusLower) {
    case "active":
      return {
        className: "status active",
        label: "Active",
      };
    case "inactive":
      return {
        className: "status inactive",
        label: "Inactive",
      };
    case "suspended":
      return {
        className: "status suspended",
        label: "Suspended",
      };
    case "pending":
      return {
        className: "status pending",
        label: "Pending",
      };
    case "inreview":
      return {
        className: "status inreview",
        label: "In Review",
      };
    default:
      return {
        className: "status unknown",
        label: status.charAt(0).toUpperCase() + status.slice(1),
      };
  }
};

// Helper function to get duty status class and label
const getDutyStatusInfo = (dutyStatus) => {
  if (!dutyStatus) return { className: "status unknown", label: "Unknown" };

  const statusLower = dutyStatus.toLowerCase();

  switch (statusLower) {
    case "available":
      return {
        className: "status available",
        label: "Available",
      };
    case "reserved":
      return {
        className: "status reserved",
        label: "Reserved",
      };
    case "on_route":
      return {
        className: "status onroute",
        label: "On Route",
      };
    case "off_duty":
      return {
        className: "status offduty",
        label: "Off Duty",
      };
    case "unavailable":
      return {
        className: "status unavailable",
        label: "Unavailable",
      };
    default:
      return {
        className: "status unknown",
        label: dutyStatus.charAt(0).toUpperCase() + dutyStatus.slice(1),
      };
  }
};

const DriverInfo = ({ driverDetails, onHide, isAdmin }) => {
  const { t } = useTranslation();
  const [updateDriverStatus, { isLoading }] = useUpdateDriverStatusMutation();

  const driverStatus = getDriverStatusInfo(driverDetails?.status);
  const dutyStatus = getDutyStatusInfo(
    driverDetails?.driver_details?.duty_status,
  );

  const handleApprove = async () => {
    toast.dismiss();
    const payload = {
      status: "active",
    };

    try {
      let response = await updateDriverStatus({
        data: payload,
        id: driverDetails.id,
      }).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.message || response.data.message);
        onHide();
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  return (
    <div className="driver-info-container">
      <div className="popupBox">
        {/* Header Section */}
        <div className="driver-header mb-4">
          <div className="d-flex align-items-center">
            <div className="driver-avatar me-3">
              {driverDetails?.profile_image ? (
                <div className="profile-image-placeholder">
                  <img
                    src={`${server}/${driverDetails.profile_image}`}
                    alt={driverDetails?.full_name}
                    className="rounded-circle"
                  />
                </div>
              ) : (
                <div className="profile-initials">
                  {driverDetails?.full_name?.charAt(0) || "D"}
                </div>
              )}
            </div>
            <div>
              <h4 className="mb-1">{driverDetails?.full_name || "N/A"}</h4>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className={`status-badge ${driverStatus.className}`}>
                  {driverStatus.label}
                </span>
                {dutyStatus.label !== "Unknown" && (
                  <span className={`status-badge ${dutyStatus.className}`}>
                    {dutyStatus.label}
                  </span>
                )}
                {driverDetails?.avg_rating != null && (
                  <span className="rating-chip">
                    ⭐ {driverDetails.avg_rating}
                    <span className="rating-chip-count">
                      {" "}
                      ({driverDetails.total_ratings}{" "}
                      {driverDetails.total_ratings === 1 ? "rating" : "ratings"}
                      )
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Information Sections */}
        <div className="info-sections">
          {/* Contact Information */}
          <div className="info-section mb-4">
            <div className="section-header mb-3">
              <MdPerson className="me-2" />
              <h5 className="mb-0">
                {t("contact")} {t("info")}
              </h5>
            </div>
            <div className="section-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="info-item">
                    <MdEmail className="info-icon" />
                    <div>
                      <label>{t("email")}</label>
                      <p>{driverDetails?.email || "N/A"}</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="info-item">
                    <FaPhoneVolume className="info-icon" />
                    <div>
                      <label>{t("phone")}</label>
                      <p>
                        {driverDetails?.phone
                          ? `+${driverDetails.phone}`
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-12 mb-3">
                  <div className="info-item">
                    <MdLanguage className="info-icon" />
                    <div>
                      <label>{t("preferred-language")}</label>
                      <p>
                        {driverDetails?.preferred_language?.toUpperCase() ||
                          "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          {driverDetails?.street && (
            <div className="info-section mb-4">
              <div className="section-header mb-3">
                <FaLocationDot className="me-2" />
                <h5 className="mb-0">
                  {t("address")} {t("info")}
                </h5>
              </div>
              <div className="section-body">
                <div className="info-item">
                  <div>
                    <label>{t("address")}</label>
                    <p>
                      {[driverDetails?.street].filter(Boolean).join(", ") ||
                        "N/A"}
                    </p>
                    {driverDetails?.zip_code && (
                      <small className="text-muted d-block mt-2">
                        {t("zip-code")}: {driverDetails.zip_code}
                      </small>
                    )}
                  </div>
                </div>
                {(driverDetails?.latitude || driverDetails?.longitude) && (
                  <div className="info-item mt-3">
                    <div>
                      <label>{t("coordinates")}</label>
                      <p className="mb-0">
                        {driverDetails?.latitude && driverDetails?.longitude
                          ? `${driverDetails.latitude}, ${driverDetails.longitude}`
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Driver License Information */}
          {driverDetails?.driver_details && (
            <div className="info-section mb-4">
              <div className="section-header mb-3">
                <FaIdCard className="me-2" />
                <h5 className="mb-0">
                  {t("license")} {t("info")}
                </h5>
              </div>
              <div className="section-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="info-item">
                      <MdCardMembership className="info-icon" />
                      <div>
                        <label>
                          {t("license")} {t("number")}
                        </label>
                        <p className="text-primary fw-bold">
                          {driverDetails.driver_details.license_number || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="info-item">
                      <MdCalendarToday className="info-icon" />
                      <div>
                        <label>
                          {t("license")} {t("expiry")}
                        </label>
                        <p
                          className={
                            new Date(
                              driverDetails.driver_details
                                .driver_licence_expiry,
                            ) < new Date()
                              ? "text-danger"
                              : "text-success"
                          }
                        >
                          {formatDateToLocal(
                            driverDetails.driver_details.driver_licence_expiry,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="info-item">
                      <MdBadge className="info-icon" />
                      <div>
                        <label>
                          {t("driver")} {t("id")}
                        </label>
                        <p>
                          #{driverDetails.driver_details.driver_id || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {driverDetails.driver_details.vehicle_id && (
                    <div className="col-md-6 mb-3">
                      <div className="info-item">
                        <MdDirectionsCar className="info-icon" />
                        <div>
                          <label>{t("assigned_vehicle")}</label>
                          <p>#{driverDetails.driver_details.vehicle_id}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* License Images */}
                {(driverDetails.driver_details.driver_licence_front ||
                  driverDetails.driver_details.driver_licence_back) && (
                  <div className="mt-3 pt-3 border-top">
                    <h6 className="mb-2">
                      {t("license")} {t("images")}
                    </h6>
                    <div className="license-images-row">
                      {driverDetails.driver_details.driver_licence_front && (
                        <div className="license-image-card">
                          <strong className="d-block mb-1">{t("front")}</strong>
                          <a
                            href={`${server}/${driverDetails.driver_details.driver_licence_front}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={`${server}/${driverDetails.driver_details.driver_licence_front}`}
                              alt="Licence Front"
                              className="licence-img"
                            />
                          </a>
                        </div>
                      )}
                      {driverDetails.driver_details.driver_licence_back && (
                        <div className="license-image-card">
                          <strong className="d-block mb-1">{t("back")}</strong>
                          <a
                            href={`${server}/${driverDetails.driver_details.driver_licence_back}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={`${server}/${driverDetails.driver_details.driver_licence_back}`}
                              alt="Licence Back"
                              className="licence-img"
                            />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Shift Information */}
          {(driverDetails?.shift_start_time ||
            driverDetails?.shift_end_time ||
            driverDetails?.shift_type) && (
            <div className="info-section mb-4">
              <div className="section-header mb-3">
                <MdUpdate className="me-2" />
                <h5 className="mb-0">{t("shift_schedule")}</h5>
              </div>
              <div className="section-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="info-item">
                      <div>
                        <label>{`${t("shift")} ${t("time")}`}</label>
                        <p className="fw-bold">
                          {convertTime(driverDetails?.shift_start_time) ||
                            "N/A"}{" "}
                          -{" "}
                          {convertTime(driverDetails?.shift_end_time) || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="info-item">
                      <div>
                        <label>{`${t("shift")} ${t("type")}`}</label>
                        <p className="text-capitalize fw-bold">
                          {driverDetails?.shift_type || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bio Section */}
          {driverDetails?.bio && (
            <div className="info-section mb-4">
              <div className="section-header mb-3">
                <MdDescription className="me-2" />
                <h5 className="mb-0">
                  {t("about")} {t("driver")}
                </h5>
              </div>
              <div className="section-body">
                <div className="bio-content p-3 bg-light rounded">
                  {driverDetails.bio}
                </div>
              </div>
            </div>
          )}

          {/* Account Information */}
          <div className="info-section">
            <div className="section-header mb-3">
              <MdCalendarToday className="me-2" />
              <h5 className="mb-0">
                {t("Account")} {t("info")}
              </h5>
            </div>
            <div className="section-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="info-item">
                    <div>
                      <label>{t("created_at")}</label>
                      <p>{formatDateToLocal(driverDetails?.created_at)}</p>
                    </div>
                  </div>
                </div>
                {driverDetails?.driver_details?.updated_at && (
                  <div className="col-md-6 mb-3">
                    <div className="info-item">
                      <div>
                        <label>
                          {t("last")} {t("updated")}
                        </label>
                        <p>
                          {formatDateToLocal(
                            driverDetails.driver_details.updated_at,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Approve Button */}
        {driverDetails?.status === "inreview" && (
          <div className="approve-section mt-4 pt-3 border-top text-center">
            <Button
              variant="success"
              size="lg"
              onClick={handleApprove}
              disabled={isLoading}
              className="px-5"
            >
              {isLoading ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">{t("loading")}...</span>
                  </div>
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  {t("approve_driver")}
                </>
              )}
            </Button>
            <p className="text-muted mt-2 mb-0">
              {t("approve_driver_message")}
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        .driver-info-container {
          font-family:
            "Inter",
            -apple-system,
            BlinkMacSystemFont,
            sans-serif;
        }

        .driver-header {
          padding-bottom: 1rem;
          border-bottom: 2px solid #f0f0f0;
        }

        .driver-avatar {
          width: 80px;
          height: 80px;
        }

        .profile-initials {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: bold;
        }

        .profile-image-placeholder {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          overflow: hidden;
          border: 3px solid #e0e0e0;
        }

        .profile-image-placeholder img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .info-sections {
          max-height: 500px;
          overflow-y: auto;
          padding-right: 10px;
        }

        .info-sections::-webkit-scrollbar {
          width: 6px;
        }

        .info-sections::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .info-sections::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }

        .info-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid #f0f0f0;
        }

        .section-header {
          display: flex;
          align-items: center;
          color: #2d3748;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .section-header svg {
          color: #4f46e5;
          font-size: 1.25rem;
        }

        .section-header h5 {
          font-weight: 600;
          font-size: 1.1rem;
          color: #2d3748;
        }

        .info-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .info-icon {
          color: #718096;
          font-size: 1.25rem;
          min-width: 24px;
          margin-top: 2px;
        }

        .info-item label {
          display: block;
          font-size: 0.875rem;
          color: #718096;
          margin-bottom: 4px;
          font-weight: 500;
        }

        .info-item p {
          margin: 0;
          font-size: 1rem;
          color: #2d3748;
          font-weight: 500;
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-block;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 1px solid;
        }

        .rating-chip {
          background: #fef3c7;
          border: 1px solid #fde68a;
          color: #92400e;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          letter-spacing: 0.3px;
        }

        .rating-chip-count {
          font-weight: 400;
          color: #b45309;
          margin-left: 2px;
        }

        .bio-content {
          font-size: 0.95rem;
          line-height: 1.6;
          color: #4a5568;
          font-style: italic;
        }

        .license-image-card {
          text-align: center;
        }

        .license-image-placeholder {
          width: 120px;
          height: 80px;
          border: 2px dashed #cbd5e0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f7fafc;
          color: #718096;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .license-image-placeholder:hover {
          border-color: #4f46e5;
          background-color: #f0f4ff;
        }

        .approve-section {
          background: linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%);
          border-radius: 12px;
          padding: 1.5rem;
        }

        @media (max-width: 768px) {
          .info-sections {
            max-height: 400px;
          }

          .driver-avatar {
            width: 60px;
            height: 60px;
          }

          .profile-initials {
            width: 60px;
            height: 60px;
            font-size: 24px;
          }

          .profile-image-placeholder {
            width: 60px;
            height: 60px;
          }

          .info-section {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default DriverInfo;
